import { useCallback, useEffect, useRef, useState } from 'react'
import {
  THRESHOLDS,
  acceptsClick,
  allLabels,
  labelFor,
  onClick as clickTransition,
  onOutcome,
  phaseFor,
  type ActionState,
  type BusyPhase,
  type Labels
} from './actionState.js'
import { ApiError } from '../net/errors.js'

/**
 * Der Aktionsknopf. Pflicht für alles, was etwas auslöst.
 *
 * Ein einfaches <button> für eine Aktion ist im Raporto-Code ein Fehler –
 * die Regeln "sofort sperren", "Ladeanzeige erst ab 400 ms", "Text ab 3 s",
 * "Ausweg ab 10 s" und "jede Netzaktion mit Zeitgrenze" halten nur, wenn sie
 * an einer Stelle liegen statt an fünfzehn.
 */

export interface ActionButtonProps {
  labels: Labels
  onAction: () => Promise<void>
  /** Klasse B: verbindlich, nie optimistisch. Klasse A: lokal, optimistisch. */
  actionClass?: 'A' | 'B'
  variant?: 'primary' | 'secondary' | 'quiet'
  disabled?: boolean
  type?: 'button' | 'submit'
  /** Wird ab 10 s angeboten, wenn gesetzt – z.B. Statusabfrage bei Klasse B. */
  onEscape?: () => void
  escapeLabel?: string
  onError?: (error: unknown) => void
  className?: string
  id?: string
}

export function ActionButton({
  labels,
  onAction,
  actionClass = 'A',
  variant = 'primary',
  disabled = false,
  type = 'button',
  onEscape,
  escapeLabel = 'Status prüfen',
  onError,
  className = '',
  id
}: ActionButtonProps): JSX.Element {
  const [state, setState] = useState<ActionState>('idle')
  const [phase, setPhase] = useState<BusyPhase>(phaseFor(0))
  const [message, setMessage] = useState('')
  // Ein Ref, keine State-Variable: der Schutz gegen den zweiten Klick muss
  // synchron greifen, nicht erst nach dem nächsten Rendern.
  const running = useRef(false)
  const startedAt = useRef(0)
  /**
   * Zwei getrennte Zeitgeber, und das ist kein Zufall:
   *
   * Die Phasen-Zeitgeber (400 ms, 3 s, 10 s) gehören zum Warten und werden
   * beendet, sobald die Aktion durch ist. Der Rückstell-Zeitgeber bringt den
   * Knopf nach der Erfolgsmeldung zurück auf bereit – hätte er denselben
   * Speicher, würde das Aufräumen am Ende der Aktion ihn gleich mitlöschen
   * und der Knopf bliebe für immer auf "erledigt" stehen.
   */
  const phaseTimers = useRef<Array<ReturnType<typeof setTimeout>>>([])
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      phaseTimers.current.forEach(clearTimeout)
      if (resetTimer.current) clearTimeout(resetTimer.current)
    }
  }, [])

  const clearPhaseTimers = useCallback(() => {
    phaseTimers.current.forEach(clearTimeout)
    phaseTimers.current = []
  }, [])

  const armPhaseTimers = useCallback(() => {
    startedAt.current = Date.now()
    setPhase(phaseFor(0))
    const at = (ms: number): void => {
      phaseTimers.current.push(
        setTimeout(() => {
          if (mounted.current) setPhase(phaseFor(Date.now() - startedAt.current))
        }, ms)
      )
    }
    at(THRESHOLDS.spinnerMs)
    at(THRESHOLDS.explainMs)
    at(THRESHOLDS.escapeMs)
  }, [])

  const handleClick = useCallback(async () => {
    if (disabled) return
    // Synchron, im selben Ereignis wie der Klick.
    if (running.current) return
    const next = clickTransition(state)
    if (!next.run) return
    running.current = true
    setState('busy')
    setMessage('')
    armPhaseTimers()

    try {
      await onAction()
      if (!mounted.current) return
      setState(onOutcome({ kind: 'success' }))
      if (resetTimer.current) clearTimeout(resetTimer.current)
      resetTimer.current = setTimeout(() => {
        resetTimer.current = null
        if (mounted.current) setState('idle')
      }, THRESHOLDS.successMs)
    } catch (err) {
      if (!mounted.current) return
      const unclear = err instanceof ApiError && err.unclear
      const text =
        err instanceof ApiError ? err.message : 'Da ist etwas schiefgelaufen. Bitte nochmal.'
      setState(onOutcome(unclear ? { kind: 'unclear', message: text } : { kind: 'error', message: text }))
      setMessage(text)
      onError?.(err)
    } finally {
      clearPhaseTimers()
      running.current = false
    }
  }, [armPhaseTimers, clearPhaseTimers, disabled, onAction, onError, state])

  const effectiveState: ActionState = disabled ? 'disabled' : state
  const label = labelFor(effectiveState, phase, labels)
  const busy = effectiveState === 'busy'
  // Auch im Erfolgszustand gesperrt: sonst sieht der Knopf in den 1,6 Sekunden
  // danach klickbar aus, schluckt den Klick aber – genau der Fall, den die
  // Regel "kein Klick läuft ins Leere" ausschliessen soll.
  const locked = busy || disabled || effectiveState === 'success'

  return (
    <span className={`action ${className}`}>
      <button
        id={id}
        type={type}
        className={`action-btn action-${variant} state-${effectiveState}${
          busy && phase.spinner ? ' with-spinner' : ''
        }`}
        disabled={locked}
        aria-busy={busy}
        aria-live="polite"
        data-state={effectiveState}
        data-class={actionClass}
        data-spinner={busy && phase.spinner ? '1' : '0'}
        onClick={() => void handleClick()}
      >
        {/*
          Alle Beschriftungen liegen übereinander im selben Raster. Die
          inaktiven sind unsichtbar, bestimmen aber die Breite mit – dadurch
          bleibt der Knopf beim Zustandswechsel exakt gleich breit.
        */}
        <span className="action-labels">
          {allLabels(labels).map((text) => (
            <span key={text} className="action-ghost" aria-hidden="true">
              {text}
            </span>
          ))}
          <span className="action-current">
            {busy && phase.spinner && <span className="action-spinner" aria-hidden="true" />}
            {label}
          </span>
        </span>
      </button>

      {busy && phase.escape && onEscape && (
        <button type="button" className="action-escape" onClick={onEscape}>
          {escapeLabel}
        </button>
      )}

      {(effectiveState === 'error' || effectiveState === 'unclear') && message && (
        <span className={`action-message ${effectiveState}`} role="status">
          {message}
        </span>
      )}
    </span>
  )
}

/** Für Tests und Bildschirme, die den Zustand selbst brauchen. */
export { acceptsClick, phaseFor, THRESHOLDS }
